import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {NumberNode} from "#template/nodes/number/number-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {visitNone} from "#template/template-node/visit-helpers"

const unaryMathOperatorTypes = ["not", "sin", "cos", "sqrt", "exp", "log", "neg", "abs", "floor", "ceil", "round"] as const
export type UnaryMathOperatorType = (typeof unaryMathOperatorTypes)[number]

function isUnaryMathOperatorType(type: MathOperatorType): type is UnaryMathOperatorType {
    return (unaryMathOperatorTypes as readonly string[]).includes(type)
}
function isBinaryMathOperatorType(type: MathOperatorType): type is BinaryMathOperatorType {
    return (binaryMathOperatorTypes as readonly string[]).includes(type)
}

function applyUnaryMathOperator(type: UnaryMathOperatorType, input: number): number {
    switch (type) {
        case "not":
            return input === 0 ? 1 : 0
        case "sin":
            return Math.sin(input)
        case "cos":
            return Math.cos(input)
        case "sqrt":
            return Math.sqrt(input)
        case "exp":
            return Math.exp(input)
        case "log":
            return Math.log(input)
        case "neg":
            return -input
        case "abs":
            return Math.abs(input)
        case "floor":
            return Math.floor(input)
        case "ceil":
            return Math.ceil(input)
        case "round":
            return Math.round(input)
    }
    throw new Error(`Unsupported unary math operator type: ${type}`)
}

function applyBinaryMathOperator(type: BinaryMathOperatorType, inputA: number, inputB: number): number {
    switch (type) {
        case "add":
            return inputA + inputB
        case "sub":
            return inputA - inputB
        case "mul":
            return inputA * inputB

        case "div":
            return inputA / inputB
        case "mod":
            return inputA % inputB
        case "gt":
            return inputA > inputB ? 1 : 0
        case "lt":
            return inputA < inputB ? 1 : 0
        case "eq":
            return inputA === inputB ? 1 : 0
        case "ne":
            return inputA !== inputB ? 1 : 0
        case "lte":
            return inputA <= inputB ? 1 : 0
        case "and":
            return inputA !== 0 && inputB !== 0 ? 1 : 0
        case "or":
            return inputA !== 0 || inputB !== 0 ? 1 : 0
        case "gte":
            return inputA >= inputB ? 1 : 0
        case "max":
            return Math.max(inputA, inputB)
        case "min":
            return Math.min(inputA, inputB)
        case "asin":
            return Math.asin(inputA / inputB)
        case "acos":
            return Math.acos(inputA / inputB)
        case "atan2":
            return Math.atan2(inputA, inputB)
    }
    throw new Error(`Unsupported binary math operator type: ${type}`)
}

const binaryMathOperatorTypes = [
    "add",
    "sub",
    "mul",
    "div",
    "mod",
    "gt",
    "lt",
    "eq",
    "ne",
    "lte",
    "and",
    "or",
    "gte",
    "max",
    "min",
    "asin",
    "acos",
    "atan2",
] as const
export type BinaryMathOperatorType = (typeof binaryMathOperatorTypes)[number]

export type MathOperatorType = UnaryMathOperatorType | BinaryMathOperatorType

const mathOperatorType: z.ZodMiniType<MathOperatorType, MathOperatorType> = z.enum([...unaryMathOperatorTypes, ...binaryMathOperatorTypes])

export type MathOperatorParameters = {
    name: string
    type: MathOperatorType
    inputA: NumberLike | null
    inputB: NumberLike | null
}

const definition: TemplateNodeDefinition<MathOperatorParameters> = {
    nodeClass: "MathOperator",
    validation: {
        parameters: z.object({
            name: z.string(),
            type: mathOperatorType,
            inputA: z.nullable(numberLike),
            inputB: z.nullable(numberLike),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MathOperator extends NumberNode<MathOperatorParameters> {
    public override get definition() {
        return definition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MathOperatorParameters> {
        const {type, inputA, inputB} = this.parameters
        if (isUnaryMathOperatorType(type) && inputA === null) return skipped
        if (isBinaryMathOperatorType(type) && (inputA === null || inputB === null)) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<number | null> {
        const {type: parameterType, inputA: parameterInputA, inputB: parameterInputB} = this.parameters

        const inputA = parameterInputA === null ? null : evaluator.evaluate(parameterInputA)
        const inputB = parameterInputB === null ? null : evaluator.evaluate(parameterInputB)

        return scope.pureLambda(
            scope.tuple(parameterType, inputA, inputB),
            ([type, inputA, inputB]) => {
                if (isUnaryMathOperatorType(type)) return inputA !== null ? applyUnaryMathOperator(type, inputA) : null
                else return inputA !== null && inputB !== null ? applyBinaryMathOperator(type, inputA, inputB) : null
            },
            "MathOperator",
        )
    }
}
